// src/pages/TeacherAnnouncements.jsx
// 授業のお知らせ（先生 → 履修者全員）（/messages/announcements）【2026-10-16】
//   ・授業を選んで、お知らせを送る・予約する・直す・取り消す
//   ・既読の数（誰が読んだかは出さない）。まだ読んでいない人にもう一度知らせる（1日1回）
//   ・画像は端末で縮めてから送る。PDF は 10MB まで
//   API: GET/POST /classes/:classId/announcements, PATCH/DELETE /announcements/:id, POST /announcements/:id/renotify
//   授業の一覧は授業管理と同じ /api/schedule/classes/overview（先生は自分の担当授業だけが返る）
import React, { useCallback, useEffect, useState } from 'react';
import { msgApi, loadFeatures } from '../messaging/api';
import {
  Page, Notice, Loading, Empty, CharCount, Tag, ReadBar, AttachmentView, AttachmentPicker, ScheduleField, ScheduleInput,
} from '../messaging/admin/ui';
import {
  fmtDateTime, isoToJstInput, jstInputToIso, checkSchedule, errText, MAX_TEXT,
} from '../messaging/admin/util';

const CLASS_KEY = 'msg.announcements.classId';

function savedClassId() {
  try { return localStorage.getItem(CLASS_KEY) || ''; } catch (_) { return ''; }
}
function saveClassId(id) {
  try { localStorage.setItem(CLASS_KEY, String(id)); } catch (_) { /* noop */ }
}

async function fetchClasses(allYears) {
  const qs = new URLSearchParams();
  if (allYears) qs.append('academicYear', 'all');
  try {
    const res = await fetch('/api/schedule/classes/overview?' + qs, {
      headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
    });
    const data = await res.json();
    if (data.success === false) return { error: data.message || '授業の一覧を取り出せませんでした' };
    return { classes: data.classes || data.data || [] };
  } catch (e) {
    return { error: '授業の一覧を取り出せませんでした' };
  }
}

// ── 書く欄 ─────────────────────────────────────────────────
function Composer({ classId, enrolled, onSent }) {
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);
  const [scheduled, setScheduled] = useState(false);
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const send = async () => {
    setMsg(null);
    const body = text.trim();
    if (!body) { setMsg({ kind: 'error', text: '本文を入れてください' }); return; }
    if (body.length > MAX_TEXT) { setMsg({ kind: 'error', text: `本文は${MAX_TEXT}文字までです` }); return; }
    let sendAt = null;
    if (scheduled) {
      const bad = checkSchedule(when);
      if (bad) { setMsg({ kind: 'error', text: bad }); return; }
      sendAt = jstInputToIso(when);
    }
    const who = enrolled != null ? `履修者 ${enrolled}人` : '履修者全員';
    const q = sendAt
      ? `${fmtDateTime(sendAt)} に、${who}へ送るよう予約します。よろしいですか？`
      : `${who}に今すぐ送ります。よろしいですか？`;
    if (!window.confirm(q)) return;

    setBusy(true);
    const path = '/classes/' + classId + '/announcements';
    const r = file
      ? await msgApi.upload(path, file, { text: body, send_at: sendAt || undefined })
      : await msgApi.post(path, sendAt ? { text: body, send_at: sendAt } : { text: body });
    setBusy(false);
    if (!r.success) { setMsg({ kind: 'error', text: errText(r) }); return; }
    setText(''); setFile(null); setScheduled(false); setWhen('');
    setMsg({ kind: 'ok', text: r.announcement && r.announcement.scheduled ? '予約しました' : '送りました' });
    onSent();
  };

  return (
    <div className="card">
      <div className="card-header"><h2 style={{ margin: 0 }}>お知らせを書く</h2></div>
      <p className="ma-lead">
        この授業の履修者全員に届きます。学生は返事できません（質問は先生への個別メッセージで受けます）。
      </p>
      <div className="ma-field">
        <div className="ma-field-label"><span>本文</span><CharCount value={text} max={MAX_TEXT} /></div>
        <textarea className="ma-textarea" value={text} onChange={e => setText(e.target.value)}
          placeholder="例）来週の授業は教室を変更します。" disabled={busy} />
      </div>
      <AttachmentPicker file={file} onChange={setFile} disabled={busy}
        onError={(t) => setMsg({ kind: 'error', text: t })} />
      <ScheduleField enabled={scheduled} onToggle={setScheduled} value={when} onChange={setWhen} idPrefix="ann" />
      {msg && <Notice kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Notice>}
      <div className="ma-actions ma-actions--end">
        <button type="button" className="btn-primary" onClick={send} disabled={busy || !text.trim()}>
          {busy ? '送っています...' : scheduled ? '予約する' : '送る'}
        </button>
      </div>
    </div>
  );
}

// ── 1件 ─────────────────────────────────────────────────────
function AnnouncementItem({ a, onChanged, onNotice }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(a.text || '');
  const [when, setWhen] = useState(isoToJstInput(a.send_at));
  const [busy, setBusy] = useState(false);

  const startEdit = () => { setText(a.text || ''); setWhen(isoToJstInput(a.send_at)); setEditing(true); };

  const saveEdit = async () => {
    const body = text.trim();
    if (!body) { onNotice({ kind: 'error', text: '本文を入れてください' }); return; }
    const payload = { text: body };
    if (a.scheduled) {
      const bad = checkSchedule(when);
      if (bad) { onNotice({ kind: 'error', text: bad }); return; }
      payload.send_at = jstInputToIso(when);
    } else if (!window.confirm('送った後に直すと「編集済み」と表示されます。通知はし直しません。直しますか？')) {
      return;
    }
    setBusy(true);
    const r = await msgApi.patch('/announcements/' + a.id, payload);
    setBusy(false);
    if (!r.success) { onNotice({ kind: 'error', text: errText(r) }); return; }
    setEditing(false);
    onNotice({ kind: 'ok', text: '直しました' });
    onChanged();
  };

  const remove = async () => {
    const q = a.scheduled
      ? 'この予約を消しますか？（まだ誰にも届いていません）'
      : 'このお知らせを取り消しますか？学生の画面から見えなくなります。';
    if (!window.confirm(q)) return;
    setBusy(true);
    const r = await msgApi.del('/announcements/' + a.id);
    setBusy(false);
    if (!r.success) { onNotice({ kind: 'error', text: errText(r) }); return; }
    onNotice({ kind: 'ok', text: a.scheduled ? '予約を消しました' : '取り消しました' });
    onChanged();
  };

  const renotify = async () => {
    const left = Math.max(0, (a.enrolled || 0) - (a.read_count || 0));
    if (!window.confirm(`まだ読んでいない ${left}人に、もう一度通知を送ります。よろしいですか？（1日1回まで）`)) return;
    setBusy(true);
    const r = await msgApi.post('/announcements/' + a.id + '/renotify');
    setBusy(false);
    if (!r.success) {
      onNotice({ kind: 'error', text: r.status === 429 ? 'もう一度知らせるのは1日1回までです' : errText(r) });
      return;
    }
    onNotice({ kind: 'ok', text: `${r.notified || 0}人にもう一度知らせました` });
  };

  const cls = 'ma-item' + (a.scheduled ? ' ma-item--scheduled' : '') + (a.retracted ? ' ma-item--retracted' : '');
  const unread = (a.enrolled || 0) - (a.read_count || 0);
  return (
    <div className={cls}>
      <div className="ma-item-head">
        {a.scheduled
          ? <><Tag tone="blue">予約</Tag><span>{fmtDateTime(a.send_at)} に送ります</span></>
          : <span>{fmtDateTime(a.sent_at || a.send_at)}</span>}
        {a.edited && <Tag tone="gray" title={a.edited_at ? fmtDateTime(a.edited_at) : ''}>編集済み</Tag>}
        {a.retracted && <Tag tone="red">取り消し済み</Tag>}
      </div>

      {editing ? (
        <div className="ma-item-edit">
          <div className="ma-field">
            <div className="ma-field-label"><span>本文</span><CharCount value={text} max={MAX_TEXT} /></div>
            <textarea className="ma-textarea" value={text} onChange={e => setText(e.target.value)} />
          </div>
          {a.scheduled && (
            <div className="ma-field">
              <div className="ma-field-label"><span>送る日時（日本時間）</span></div>
              <ScheduleInput value={when} onChange={setWhen} />
            </div>
          )}
          <div className="ma-actions ma-actions--end">
            <button type="button" className="btn-secondary" onClick={() => setEditing(false)} disabled={busy}>やめる</button>
            <button type="button" className="btn-primary" onClick={saveEdit} disabled={busy || !text.trim()}>
              {busy ? '保存中...' : '保存する'}
            </button>
          </div>
        </div>
      ) : a.retracted ? (
        <div className="ma-item-text ma-item-text--gone">このお知らせは取り消しました。</div>
      ) : (
        <>
          <div className="ma-item-text">{a.text}</div>
          <AttachmentView image={a.image} file={a.file} />
        </>
      )}

      {!a.retracted && !editing && (
        <div className="ma-item-foot">
          {a.scheduled
            ? <span className="ma-muted" style={{ fontSize: '0.84rem' }}>まだ送っていません</span>
            : <ReadBar read={a.read_count} total={a.enrolled} />}
          <div className="ma-actions">
            {!a.scheduled && unread > 0 && (
              <button type="button" className="btn-secondary ma-btn-sm" onClick={renotify} disabled={busy}>
                まだ読んでいない人にもう一度知らせる
              </button>
            )}
            <button type="button" className="btn-secondary ma-btn-sm" onClick={startEdit} disabled={busy}>直す</button>
            <button type="button" className="btn-danger ma-btn-sm" onClick={remove} disabled={busy}>
              {a.scheduled ? '予約を消す' : '取り消す'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── 画面 ─────────────────────────────────────────────────────
export default function TeacherAnnouncements({ user }) {
  const [featureOn, setFeatureOn] = useState(null);
  const [classes, setClasses] = useState(null);
  const [allYears, setAllYears] = useState(false);
  const [classErr, setClassErr] = useState('');
  const [classId, setClassId] = useState(savedClassId());
  const [list, setList] = useState(null);
  const [enrolled, setEnrolled] = useState(null);
  const [listErr, setListErr] = useState('');
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    let alive = true;
    loadFeatures().then(f => { if (alive) setFeatureOn(!!(f.features && f.features.announcements)); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setClasses(null);
    fetchClasses(allYears).then(r => {
      if (!alive) return;
      if (r.error) { setClassErr(r.error); setClasses([]); return; }
      setClassErr('');
      const cs = (r.classes || []).filter(c => !c.archived || allYears);
      setClasses(cs);
      setClassId(cur => (cs.some(c => String(c.id) === String(cur)) ? cur : (cs[0] ? String(cs[0].id) : '')));
    });
    return () => { alive = false; };
  }, [allYears]);

  const loadList = useCallback(async () => {
    if (!classId) { setList([]); return; }
    const r = await msgApi.get('/classes/' + classId + '/announcements');
    if (!r.success) { setListErr(errText(r)); setList([]); return; }
    setListErr('');
    setEnrolled(r.enrolled != null ? r.enrolled : null);
    setList(r.announcements || []);
  }, [classId]);

  useEffect(() => {
    setList(null); setEnrolled(null); setNotice(null);
    if (classId) saveClassId(classId);
    loadList();
  }, [classId, loadList]);

  const current = classes && classes.find(c => String(c.id) === String(classId));

  return (
    <Page title="授業のお知らせ" lead="授業の履修者全員に、お知らせを送ります。読んだ人の数が分かります（誰が読んだかは出しません）。">
      {featureOn === false && (
        <Notice kind="warn">授業のお知らせは、まだ使えるようになっていません。使えるようになるまでお待ちください。</Notice>
      )}

      <div className="card">
        <div className="ma-actions" style={{ alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 260px' }}>
            <div className="ma-field-label"><span>授業</span></div>
            {classes === null ? <Loading text="授業を読み込み中..." /> : classes.length === 0 ? (
              <div className="ma-muted">担当している授業がありません。</div>
            ) : (
              <select className="ma-select ma-input--full" value={classId} onChange={e => setClassId(e.target.value)}>
                {classes.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.term_name ? `（${c.academic_year ? c.academic_year + '年度 ' : ''}${c.term_name}）` : c.academic_year ? `（${c.academic_year}年度）` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <label className="ma-check" style={{ paddingBottom: '0.5rem' }}>
            <input type="checkbox" checked={allYears} onChange={e => setAllYears(e.target.checked)} />
            ほかの年度の授業も出す
          </label>
        </div>
        {classErr && <div style={{ marginTop: '0.75rem' }}><Notice kind="error">{classErr}</Notice></div>}
        {current && enrolled != null && (
          <div className="ma-muted" style={{ marginTop: '0.6rem' }}>履修者 {enrolled}人</div>
        )}
      </div>

      {classId && featureOn && (
        <Composer classId={classId} enrolled={enrolled} onSent={loadList} />
      )}

      {classId && (
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
            <h2 style={{ margin: 0 }}>送ったお知らせ</h2>
            <button type="button" className="btn-secondary ma-btn-sm" onClick={loadList}>最新にする</button>
          </div>
          {notice && <Notice kind={notice.kind} onClose={() => setNotice(null)}>{notice.text}</Notice>}
          {listErr && <Notice kind="error">{listErr}</Notice>}
          {list === null ? <Loading /> : list.length === 0 ? (
            !listErr && <Empty>この授業には、まだお知らせがありません。</Empty>
          ) : (
            <div className="ma-list">
              {list.map(a => (
                <AnnouncementItem key={a.id + ':' + (a.edited_at || '') + ':' + (a.send_at || '')}
                  a={a} onChanged={loadList} onNotice={setNotice} />
              ))}
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
